import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { SocialLinks } from '../components/SocialLinks';

export default function PrivacyPage() {
  const { user } = useAuth();
  const backPath = user
    ? user.role === 'admin' ? '/admin' : user.role === 'driver' ? '/driver' : '/customer'
    : '/';
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to={backPath} className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900" data-testid="privacy-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to JustDeliver
          </Link>
          <Link to={backPath}>
            <img src="/logo.png" alt="JustDeliver" className="h-7 w-auto" />
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 sm:py-16" data-testid="privacy-page">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "'Inter', sans-serif" }}>
          Privacy Policy
        </h1>
        <p className="mt-2 text-sm text-gray-400">Last updated: {new Date().toLocaleDateString('en-IE', { month: 'long', year: 'numeric' })}</p>

        <div className="mt-10 space-y-10 text-[15px] leading-relaxed text-gray-600">
          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">1. Data We Collect</h2>
            <p>When you use JustDeliver, we collect the following information:</p>
            <ul className="mt-2 space-y-1.5 list-disc pl-5">
              <li><strong>Account details:</strong> Name, email address, phone number, and role (customer or driver)</li>
              <li><strong>Delivery information:</strong> Pickup and drop-off addresses, item descriptions, photos, and receiver details</li>
              <li><strong>Driver information:</strong> Location/city, profile photo, bio, and Stripe Connect payout details</li>
              <li><strong>Payment data:</strong> Processed securely by Stripe — we do not store your card details</li>
              <li><strong>Usage data:</strong> How you interact with the platform (pages visited, actions taken)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">2. How We Use Your Data</h2>
            <p>Your data is used to:</p>
            <ul className="mt-2 space-y-1.5 list-disc pl-5">
              <li>Match customers with drivers and facilitate deliveries</li>
              <li>Process payments securely through Stripe</li>
              <li>Send important notifications (job updates, delivery status, verification codes)</li>
              <li>Provide customer support</li>
              <li>Improve the platform and user experience</li>
              <li>Prevent fraud and ensure platform safety</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">3. Data Sharing</h2>
            <p>We share your data only in limited circumstances:</p>
            <ul className="mt-2 space-y-1.5 list-disc pl-5">
              <li><strong>Between users:</strong> When a job is accepted, customers can see the driver's name, phone number, and profile. Drivers can see the customer's name, phone, and delivery details</li>
              <li><strong>Stripe:</strong> Payment and payout data is shared with Stripe to process transactions</li>
              <li><strong>Email provider:</strong> Your email is used to send transactional emails (verification, notifications)</li>
            </ul>
            <p className="mt-3 font-semibold text-gray-900">We do NOT sell your personal data to third parties. Ever.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">4. Data Storage & Security</h2>
            <ul className="space-y-1.5 list-disc pl-5">
              <li>Your data is stored securely using industry-standard encryption and access controls</li>
              <li>Passwords are hashed using bcrypt and are never stored in plain text</li>
              <li>Authentication uses secure, HTTP-only cookies</li>
              <li>We retain your data only as long as your account is active or as required by law</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">5. Cookies</h2>
            <p>JustDeliver uses essential cookies to:</p>
            <ul className="mt-2 space-y-1.5 list-disc pl-5">
              <li>Keep you logged in securely</li>
              <li>Remember your session preferences</li>
              <li>Ensure the platform works correctly</li>
            </ul>
            <p className="mt-2">We do not use tracking or advertising cookies.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">6. Your Rights (GDPR)</h2>
            <p>Under the General Data Protection Regulation (GDPR), you have the right to:</p>
            <ul className="mt-2 space-y-1.5 list-disc pl-5">
              <li><strong>Access</strong> — Request a copy of the personal data we hold about you</li>
              <li><strong>Correction</strong> — Ask us to correct any inaccurate information</li>
              <li><strong>Deletion</strong> — Request that we delete your account and associated data</li>
              <li><strong>Restrict processing</strong> — Ask us to limit how we use your data</li>
              <li><strong>Data portability</strong> — Request your data in a portable format</li>
              <li><strong>Withdraw consent</strong> — Withdraw consent at any time by contacting us</li>
            </ul>
            <p className="mt-3">To exercise any of these rights, please contact us at <a href="mailto:mark@justdeliver.ie" className="text-[#2E7D32] font-medium hover:underline">mark@justdeliver.ie</a>.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">7. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. Changes will be posted on this page. Continued use of JustDeliver after updates are published constitutes acceptance.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">8. Contact Us</h2>
            <p>For any privacy-related questions or requests:</p>
            <ul className="mt-2 space-y-1.5">
              <li>Email: <a href="mailto:mark@justdeliver.ie" className="text-[#2E7D32] font-medium hover:underline">mark@justdeliver.ie</a></li>
              <li>WhatsApp: <a href="https://wa.me/353899598628" className="text-[#2E7D32] font-medium hover:underline">089 959 8628</a></li>
            </ul>
          </section>
        </div>
      </main>

      <footer className="py-8 border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="JustDeliver" className="h-8 w-auto" />
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="/terms" className="hover:text-gray-900">Terms</Link>
            <Link to="/privacy" className="hover:text-gray-900">Privacy</Link>
          </div>
          <SocialLinks />
        </div>
      </footer>
    </div>
  );
}
